import React, { useState } from 'react';
import { LuCalendarPlus, LuExternalLink, LuListChecks } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import { Field, TextArea, TextInput } from '../../components/form/FormControls';
import { useTranslation } from '../../i18n/LanguageContext';
import { CARD, HelpNote, SectionHeader } from './components/ProfileUi';

const EMPTY_EVENT = { title: '', description: '', startDateTime: '', endDateTime: '' };

// datetime-local alanının "şu an" değeri (yerel saat)
const nowLocal = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

/**
 * "Ajandam": prepares an event in Google Calendar (no sign-in needed: the
 * Google Calendar page opens pre-filled, the user saves it there) and links
 * to the user's own Google Calendar.
 */
const Agenda = () => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.agenda.${key}`, vars);
  const [event, setEvent] = useState(EMPTY_EVENT);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const addToGoogleCalendar = () => {
    if (!event.title.trim()) {
      setError(label('errors.title'));
      return;
    }
    if (!event.startDateTime) {
      setError(label('errors.start'));
      return;
    }
    if (!event.endDateTime) {
      setError(label('errors.end'));
      return;
    }
    const startDate = new Date(event.startDateTime);
    const endDate = new Date(event.endDateTime);
    if (endDate <= startDate) {
      setError(label('errors.order'));
      return;
    }
    if (startDate < new Date()) {
      setError(label('errors.past'));
      return;
    }
    setError('');

    try {
      const formatDateForGoogle = (date) => `${date.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
      const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}&details=${encodeURIComponent(event.description || '')}&location=&sf=true&output=xml`;
      window.open(googleCalendarUrl, '_blank');
      setSuccess(label('opened'));
      setEvent(EMPTY_EVENT);
    } catch (err) {
      console.error('Error creating calendar URL:', err);
      setError(label('errors.url'));
    }
  };

  const steps = ['fill', 'add', 'save', 'done'];

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuCalendarPlus}
        gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
        title={label('title')}
        description={label('description')}
      >
        <Button size="sm" onClick={() => window.open('https://calendar.google.com', '_blank')}>
          <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('openCalendar')}
        </Button>
      </SectionHeader>

      <div className="tw-grid tw-gap-4 lg:tw-grid-cols-[minmax(0,1fr)_18rem]">
        <form
          className={`${CARD} tw-grid tw-gap-4 tw-p-4 sm:tw-grid-cols-2 sm:tw-p-5`}
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();
            addToGoogleCalendar();
          }}
        >
          <Field label={label('eventTitle')} htmlFor="agenda-title" required className="sm:tw-col-span-2">
            <TextInput id="agenda-title" value={event.title} onChange={(e) => setEvent({ ...event, title: e.target.value })} placeholder={label('eventTitlePlaceholder')} />
          </Field>
          <Field label={label('eventDescription')} htmlFor="agenda-description" className="sm:tw-col-span-2">
            <TextArea id="agenda-description" rows={3} value={event.description} onChange={(e) => setEvent({ ...event, description: e.target.value })} placeholder={label('eventDescriptionPlaceholder')} />
          </Field>
          <Field label={label('start')} htmlFor="agenda-start" required>
            <TextInput id="agenda-start" type="datetime-local" min={nowLocal()} value={event.startDateTime} onChange={(e) => setEvent({ ...event, startDateTime: e.target.value })} />
          </Field>
          <Field label={label('end')} htmlFor="agenda-end" required>
            <TextInput id="agenda-end" type="datetime-local" min={event.startDateTime || nowLocal()} value={event.endDateTime} onChange={(e) => setEvent({ ...event, endDateTime: e.target.value })} />
          </Field>

          {error && <HelpNote tone="danger" className="sm:tw-col-span-2">{error}</HelpNote>}
          {success && !error && <HelpNote className="sm:tw-col-span-2">{success}</HelpNote>}

          <div className="sm:tw-col-span-2">
            <Button variant="primary" type="submit" className="tw-w-full">
              <LuCalendarPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('add')}
            </Button>
          </div>
        </form>

        <aside className={`${CARD} tw-space-y-3 tw-p-4 sm:tw-p-5`}>
          <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
            <LuListChecks aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-500" />
            {label('howTo')}
          </p>
          <ol className="tw-space-y-2.5">
            {steps.map((step, index) => (
              <li key={step} className="tw-flex tw-items-start tw-gap-2.5 tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                <span className="tw-grid tw-h-6 tw-w-6 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-teal-500/10 tw-text-xs tw-font-bold tw-text-teal-700 dark:tw-text-teal-300">{index + 1}</span>
                <span className="tw-pt-0.5">{label(`steps.${step}`)}</span>
              </li>
            ))}
          </ol>
          <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('note')}</p>
        </aside>
      </div>
    </div>
  );
};

export default Agenda;
